<style lang="scss" scoped>
    .trial-banner {
        top: 64px;
        margin-left: 235px;
        border-radius: 0;
    }
</style>

<template>
    <div>
        <v-alert
            class="mb-0 trial-banner text-center"
            text
            dense
            :color="color">
            <v-row align="center">
                <v-col class="grow">
                    <template v-if="!!creditLeft">
                        <template v-if="isServerlessAvailable">
                            {{ $t('Service.creditAndSCULeft', [creditLeft, scuHoursLeft]) }}
                        </template>
                        <template v-else>
                            {{ $t('Service.creditLeft', [creditLeft]) }}
                        </template>
                    </template>
                    <template v-else-if="isServerlessAvailable">
                        <template v-if="diskUsageData">
                            {{ $t('Service.serverlessUsageStatus', [scuHoursConsumed, diskUsed]) }}
                        </template>
                        <template v-else>
                            {{ $t('Service.trialSCUUsed', [scuHoursConsumed]) }}
                        </template>
                    </template>
                </v-col>
                <v-col class="d-flex shrink text-no-wrap align-center">
                    <span class="font-weight-bold mr-2">
                        {{ $t('unlockMore') }}:
                    </span>
                    <v-btn
                        :color="color"
                        small
                        outlined
                        :loading="isPaymentMethodInProcess || isFetchingPaymentDetail"
                        @click="openPaymentDialog">
                        {{ $t('billings.addCreditCard') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <PaymentModal
            v-if="showPaymentDialog"
            :payment-profile="paymentProfile"
            :legal-entity-identifier="legalEntityIdentifier"
            :primary-contact-country="billingRecord.primaryContactCountry"
            @on-update-profile="handleProfileUpdate"
            @on-payment-close="handleProfileClose" />
    </div>
</template>
<script>
    import { mapGetters, mapActions } from 'vuex'
    import { isServerlessTopology } from 'utils/service'
    import { getLegalEntityIdentifier } from 'utils/billing'
    import { getMetricsData } from 'services/monitoring.js'
    import PaymentModal from 'components/billing/paymentManagement/Payment.vue'

    export default {
        name: 'FreeTrialBanner',
        components: {
            PaymentModal,
        },
        props: {
            creditLeft: {
                type: Number,
                default: 0,
            },
            scuHoursLeft: {
                type: Number,
                default: 0,
            },
            scuHoursConsumed: {
                type: Number,
                default: 0,
            },
        },
        data() {
            return {
                diskUsageData: null,
                diskUsed: 0,
                showPaymentDialog: false,
                isPaymentMethodInProcess: false,
            }
        },
        computed: {
            ...mapGetters([
                'regions',
                'services',
                'billingRecord',
                'paymentProfile',
                'billingCountries',
                'isFetchingPaymentDetail'
            ]),
            legalEntityIdentifier() {
                return getLegalEntityIdentifier(this.billingRecord)
            },
            color() {
                if (this.creditLeft <= 25 && this.scuHoursLeft <= 2.5) {
                    return 'error'
                }
                if (this.creditLeft <= 50 && this.scuHoursLeft <= 5) {
                    return 'warning'
                }
                return 'teal'
            },
            isServerlessAvailable() {
                return this.$unleash.isFeatureEnabled('enable-serverless')
            },
        },
        watch: {
            services(){
                if (!this.services?.length) return
                const serverlessInstance = this.services.filter(
                    service => isServerlessTopology(service.topology) && service.status === this.$config.serviceStatus.READY
                )?.[0]
                if (!serverlessInstance) return
                this.fetchMetricsData(serverlessInstance)
            },
        },
        methods: {
            ...mapActions([
                'fetchBillingRecord',
                'fetchPaymentMethods',
                'fetchCountries',
                'togglePaymentDialog',
                'getRegions'
            ]),
            async handleProfileUpdate() {
                await this.fetchPaymentMethods()
                await this.fetchBillingRecord()
                this.getRegions()
                this.handleProfileClose()
            },
            handleProfileClose() {
                this.togglePaymentDialog(false)
                this.showPaymentDialog = false
            },
            async openPaymentDialog() {
                this.isPaymentMethodInProcess = true
                if (this.$lodash.isEmpty(this.paymentProfile)) {
                    await this.fetchPaymentMethods()
                }
                this.showPaymentDialog = true
                if (this.$lodash.isEmpty(this.billingCountries)) {
                    await this.fetchCountries()
                } else {
                    // Minor delay is required to open the
                    // payment dialog for cached data
                    await this.$nextTick()
                }
                this.isPaymentMethodInProcess = false
                this.togglePaymentDialog(true)
            },
            hasData (data = []) {
                for (let i = 0; i < data.length; i++) {
                    if (!this.$typy(data[i], 'result').isEmptyArray) return true
                }
                return false
            },
            isDataValid (data = []) {
                for (let i = 0; i < data.length; i++) {
                    const { resultType, result, } = data[i]
                    for (let ri = 0; ri < result.length; ri++) {
                        if (this.hasNanValues(resultType === 'vector' ? result[ri].value : result[ri].values)) return false
                    }
                }
                return true
            },
            hasNanValues (values = []) {
                return values.flat().includes('NaN')
            },
            async fetchMetricsData (service) {
                try {
                    await this.getRegions()
                    const metric = [{
                        'name': 'serviceDiskSizeUsedBy',
                        'legend': '{{ kubernetes_pod_name }}',
                        'instant': true,
                        'diskPurpose': 'data',
                        'byServer': true,
                    }]
                    const metricsOptions = {
                        id: service.namespace || this.service.id,
                        region: service.region,
                        name: service.name,
                        servers: service.servers,
                    }
                    const data = await getMetricsData(
                        metric,
                        metricsOptions
                    )
                    if (this.hasData(data) && this.isDataValid(data)) {
                        this.diskUsageData = data
                        const bytes = data?.[0]?.result?.[0]?.value?.[1] || 0
                        const gb = bytes / (1024 ** 3)
                        this.diskUsed = gb.toFixed(2)
                    } else {
                        this.diskUsageData = {}
                        this.diskUsed = 0
                    }
                } catch (error) {
                    //
                }
            },
        },
    }
</script>
